import { Component, computed, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BaseService } from 'src/app/services/base.service';
import { QRISLY_WEBHOOK } from 'src/app/constants/endpoint.const';
import { FormValidationService } from 'src/app/services/form-validation.service';
import { ModalAddWebhookComponent } from 'src/app/pages/integration/referrer/components/modal-add-webhook/modal-add-webhook.component';
import { map } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-outbond-webhook',
  templateUrl: './outbond-webhook.component.html',
  styleUrl: './outbond-webhook.component.scss'
})
export class OutbondWebhookComponent implements OnInit {
  isGetWebhook = input<boolean>(true);
  upgrade = output<void>();

  private message = inject(NzMessageService);
  private modalService = inject(NgbModal);
  private baseService = inject(BaseService);
  private validationService = inject(FormValidationService);
  private destroyRef = inject(DestroyRef);

  listWebhook = signal<string>('');
  loading = signal<boolean>(true);
  isEditMode = signal<boolean>(false);

  formGroupWebhook: FormGroup = new FormGroup({
    url: new FormControl('', []),
  });

  private urlValue = toSignal(
    this.formGroupWebhook.get('url')!.valueChanges.pipe(map(v => v as string)),
    { initialValue: '' }
  );

  isValidURL = signal<boolean>(true);
  errPN = signal<string>('');
  isSame = signal<boolean>(true);

  isSaveDisabled = computed(() => {
    return (
      this.errPN() !== '' ||
      this.isSame() ||
      !this.urlValue()
    );
  });

  constructor() {
    this.formGroupWebhook.get('url')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setValueRuleWebhook();
      });
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.loading.set(false);
    }, 1000);
  }

  setValueRuleWebhook() {
    const value = this.formGroupWebhook.get('url')?.value || '';
    const result = this.validationService.validateUrl(value);
    this.isValidURL.set(result.valid);
    this.errPN.set(result.message || '');

    if (value === this.listWebhook()) {
      this.isSame.set(true);
    } else {
      this.isSame.set(false);
    }
  }

  toggleEdit() {
    this.isEditMode.set(true);
    this.formGroupWebhook.get('url')?.enable();
  }

  submit() {
    const params = {
      webhook_url: this.formGroupWebhook.get('url')?.value,
    };
    this.baseService
      .putData(QRISLY_WEBHOOK, params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.create(
            'success',
            'Your webhook url has been successfully saved.'
          );
          this.getWebhook();
          this.isSame.set(true);
        },
        error: (err) => {
          this.message.create('error', err.error.meta.message);
        }
      });
  }

  addWebhook() {
    const modal = this.modalService.open(ModalAddWebhookComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
    });

    const modalInstance = modal.componentInstance as ModalAddWebhookComponent;
    modalInstance.typeWebhook = 'qrisly';

    modalInstance.updateWebhook
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.getWebhook();
      });
  }

  getWebhook() {
    this.baseService
      .getData(QRISLY_WEBHOOK)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const webhookUrl = res.data?.webhook_url || '';
          this.listWebhook.set(webhookUrl);
          this.formGroupWebhook.get('url')?.setValue(webhookUrl);

          if (webhookUrl) {
            this.formGroupWebhook.get('url')?.disable();
            this.isEditMode.set(false);
          } else {
            this.formGroupWebhook.get('url')?.enable();
            this.isEditMode.set(true);
          }
        },
        error: (err) => {
          this.message.create('error', err.error.meta.message || 'Failed to get Webhook data');
        }
      });
  }

  upgradeToEnterprise() {
    this.upgrade.emit();
  }
}
